import React, { useState, useEffect } from 'react';
import './Trump.css';

// All dates are US Eastern time.
// Congress flip: 2026 midterm election day, and the new Congress convening
// at noon on January 3 (20th Amendment).
const CONGRESS_VOTE = new Date('2026-11-03T00:00:00-05:00');
const CONGRESS_SWORN_IN = new Date('2027-01-03T12:00:00-05:00');
// End of Trump's second term: 2028 presidential election day, and the
// inauguration at noon on January 20, 2029.
const PRESIDENT_VOTE = new Date('2028-11-07T00:00:00-05:00');
const PRESIDENT_SWORN_IN = new Date('2029-01-20T12:00:00-05:00');
// Start of the Iran War: February 28, 2026
const IRAN_WAR_START = new Date('2026-02-28T00:00:00');

const pad = (n) => String(n).padStart(2, '0');

// Add n calendar months, clamping to the last day of the resulting month
// (so e.g. Jan 31 + 1 month = Feb 28, not Mar 3).
const addMonths = (date, n) => {
  const d = new Date(date);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));
  return d;
};

const getCountdown = (target) => {
  const now = new Date();
  if (target - now <= 0) {
    return { years: 0, months: '00', days: '00', hours: '00', minutes: '00', seconds: '00' };
  }
  let totalMonths =
    (target.getFullYear() - now.getFullYear()) * 12 + (target.getMonth() - now.getMonth());
  while (totalMonths > 0 && addMonths(now, totalMonths) > target) {
    totalMonths--;
  }
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  let diff = target - addMonths(now, totalMonths);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  diff %= 1000 * 60 * 60 * 24;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  diff %= 1000 * 60 * 60;
  const minutes = Math.floor(diff / (1000 * 60));
  diff %= 1000 * 60;
  const seconds = Math.floor(diff / 1000);
  return {
    years,
    months: pad(months),
    days: pad(days),
    hours: pad(hours),
    minutes: pad(minutes),
    seconds: pad(seconds),
  };
};

const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

const formatWeeks = (totalDays) => {
  const weeks = Math.floor(totalDays / 7);
  const days = totalDays % 7;
  return days ? `${plural(weeks, 'week')}, ${plural(days, 'day')}` : plural(weeks, 'week');
};

const formatTrillions = (amount) => `$${(amount / 1e12).toFixed(2)} trillion`;

const formatRecordDate = (isoDate) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const getDaysSince = (start) => Math.max(0, Math.floor((new Date() - start) / (1000 * 60 * 60 * 24)));

const formatEventDate = (date) =>
  date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/New_York',
  });

const Countdown = ({ label, date, countdown }) => (
  <div className="trump-countdown-group">
    <p className="trump-countdown-heading">
      {label}: {formatEventDate(date)}
    </p>
    <p className="trump-countdown">
      {countdown.years}:{countdown.months}:{countdown.days}:{countdown.hours}:{countdown.minutes}:{countdown.seconds}
    </p>
    <p className="trump-countdown-labels">years : months : days : hours : minutes : seconds</p>
  </div>
);

const getCountdowns = () => ({
  congressVote: getCountdown(CONGRESS_VOTE),
  congressSwornIn: getCountdown(CONGRESS_SWORN_IN),
  presidentVote: getCountdown(PRESIDENT_VOTE),
  presidentSwornIn: getCountdown(PRESIDENT_SWORN_IN),
});

const Trump = () => {
  const [countdowns, setCountdowns] = useState(getCountdowns);
  const [iranWarDays, setIranWarDays] = useState(() => getDaysSince(IRAN_WAR_START));
  const [debt, setDebt] = useState(null);
  const [debtError, setDebtError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/debt', { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setDebt(data);
      })
      .catch(() => {
        if (!cancelled) setDebtError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const tick = () => {
      setCountdowns(getCountdowns());
      setIranWarDays(getDaysSince(IRAN_WAR_START));
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="trump">
      <h1>Trump countdowns</h1>

      <section className="trump-block">
        <h2 className="trump-block-title">Next chance to flip Congress</h2>
        <Countdown label="Vote" date={CONGRESS_VOTE} countdown={countdowns.congressVote} />
        <Countdown label="Sworn in" date={CONGRESS_SWORN_IN} countdown={countdowns.congressSwornIn} />
      </section>

      <section className="trump-block">
        <h2 className="trump-block-title">Trump's term is done</h2>
        <Countdown label="Vote" date={PRESIDENT_VOTE} countdown={countdowns.presidentVote} />
        <Countdown label="Sworn in" date={PRESIDENT_SWORN_IN} countdown={countdowns.presidentSwornIn} />
      </section>

      <section className="trump-block">
        <h2 className="trump-block-title">Days since start of Iran War</h2>
        <p className="trump-countdown">{iranWarDays.toLocaleString()}</p>
        <p className="trump-countdown-secondary">
          {formatWeeks(iranWarDays)}
        </p>
        <p className="trump-note">(since February 28, 2026)</p>
      </section>

      <section className="trump-block">
        <h2 className="trump-block-title">US debt added since Trump's second term began</h2>
        {debt ? (
          <>
            <p className="trump-countdown">{formatTrillions(debt.added)}</p>
            <p className="trump-note">
              {formatTrillions(debt.baseline.amount)} on {formatRecordDate(debt.baseline.record_date)}
              {' → '}
              {formatTrillions(debt.latest.amount)} on {formatRecordDate(debt.latest.record_date)}
            </p>
            <p className="trump-note">
              Source:{' '}
              <a
                href="https://fiscaldata.treasury.gov/datasets/debt-to-the-penny/"
                target="_blank"
                rel="noopener noreferrer"
              >
                U.S. Treasury, Debt to the Penny
              </a>
            </p>
          </>
        ) : (
          <p className="trump-note">{debtError ? 'Debt data is unavailable right now.' : 'Loading…'}</p>
        )}
      </section>

      <p className="trump-link">
        <a href="https://x.com/DaysLeft4Trump" target="_blank" rel="noopener noreferrer">
          Follow @DaysLeft4Trump on X
        </a>
      </p>
    </div>
  );
};

export default Trump;
